// @ts-strict-ignore
import type { LegacyAction, LegacyMatrixAxisDataRoutes, LegacyMatrixModelResponse } from '../../../store/engine/types';
import { isMatrix1Dimensional } from './matrixModels';
import { MatrixAxisDataType, MatrixAxisOrder, MatrixDataType } from './types';

export const fillEmptyAxisValues = (
  axisHeader: LegacyMatrixModelResponse['x_axis_header' | 'y_axis_header'],
  dataType: LegacyMatrixModelResponse[
    'x_axis_header_data_type' | 'y_axis_header_data_type'
  ],
  order: LegacyMatrixModelResponse['x_order' | 'y_order'],
): LegacyMatrixModelResponse['x_axis_header' | 'y_axis_header'] => {
  let numberToFillNextEmptyAxis = 0;

  return axisHeader.map(value => {
    if (dataType === MatrixAxisDataType.STRING) {
      return value ?? '';
    }

    if (value || value === 0) {
      numberToFillNextEmptyAxis = order === MatrixAxisOrder.ASC
        ? Number(value) + 1 : Number(value) - 1;
      return value;
    }

    return numberToFillNextEmptyAxis;
  });
};

export const fillEmptyMatrixValues = (
  matrix: LegacyMatrixModelResponse['matrix'],
  matrixDataType: LegacyMatrixModelResponse['matrix_data_type'],
): LegacyMatrixModelResponse['matrix'] => {
  if (matrixDataType === MatrixDataType.NUMBER) {
    return matrix.map(row => row.map(value => value ?? null));
  }

  return matrix.map(row => row.map(value => value ?? ''));
};

export const fillEmptyAxisAndMatrixValues = (
  matrixModel: LegacyMatrixModelResponse,
): LegacyMatrixModelResponse => {
  const {
    x_axis_header: xAxisHeader,
    y_axis_header: yAxisHeader,
    x_axis_header_data_type: xAxisDataType,
    y_axis_header_data_type: yAxisDataType,
    x_order: xAxisOrder,
    y_order: yAxisOrder,
    matrix,
    matrix_data_type: matrixDataType,
  } = matrixModel;

  const updatedXAxisHeader = fillEmptyAxisValues(
    xAxisHeader, xAxisDataType, xAxisOrder,
  );
  const updatedYAxisHeader = fillEmptyAxisValues(
    yAxisHeader, yAxisDataType, yAxisOrder,
  );
  const updatedMatrix = fillEmptyMatrixValues(
    matrix, matrixDataType,
  );

  return {
    ...matrixModel,
    x_axis_header: updatedXAxisHeader,
    y_axis_header: updatedYAxisHeader,
    matrix: updatedMatrix,
  };
};

export const isAxisHeaderSorted = (
  axisHeader: LegacyMatrixModelResponse['x_axis_header' | 'y_axis_header'],
  order: LegacyMatrixModelResponse['x_order' | 'y_order'],
): boolean => {
  for (let i = 1; i < axisHeader.length; i += 1) {
    const current = Number(axisHeader[i]);
    const previous = Number(axisHeader[i - 1]);
    if (order === MatrixAxisOrder.ASC && previous > current) {
      return false;
    }
    if (order === MatrixAxisOrder.DESC && previous < current) {
      return false;
    }
    if (previous === current) return false;
  }
  return true;
};

export const isAxisHeaderUnique = (
  axisHeader: LegacyMatrixModelResponse['x_axis_header' | 'y_axis_header'],
) => {
  const uniqueValues = new Set();
  for (let i = 0; i < axisHeader.length; i += 1) {
    const value = axisHeader[i];
    if (uniqueValues.has(value)) return false;
    uniqueValues.add(value);
  }
  return true;
};

export const isAxisValid = (
  axisHeader: LegacyMatrixModelResponse['x_axis_header' | 'y_axis_header'],
  dataType: LegacyMatrixModelResponse[
    'x_axis_header_data_type' | 'y_axis_header_data_type'
  ],
  order: LegacyMatrixModelResponse['x_order' | 'y_order'],
) => {
  switch (dataType) {
    case MatrixDataType.NUMBER:
      if (!isAxisHeaderSorted(axisHeader, order)) return false;
      break;
    case MatrixDataType.STRING:
      if (!isAxisHeaderUnique(axisHeader)) return false;
      break;
  }
  return true;
};

// This function validates user errors for axis headers
// Returns null for no error or string for error message
export const validateMatrixModelsAxis = (
  matrixModel: LegacyMatrixModelResponse,
): string => {
  const {
    name,
    x_axis_header: xAxisHeader,
    y_axis_header: yAxisHeader,
    x_axis_header_data_type: xAxisDataType,
    y_axis_header_data_type: yAxisDataType,
    x_order: xAxisOrder,
    y_order: yAxisOrder,
  } = matrixModel;

  const invalid = { x: false, y: false };
  if (!isAxisValid(xAxisHeader, xAxisDataType, xAxisOrder)) {
    invalid.x = true;
  }
  if (!isAxisValid(yAxisHeader, yAxisDataType, yAxisOrder)) {
    invalid.y = true;
  }

  if (invalid.x || invalid.y) {
    const axisErrorMsg = invalid.x && invalid.y
      ? 'X and Y axes are'
      : `${invalid.x ? 'X' : 'Y'} axis is`;

    return `Matrix ${name}: ${axisErrorMsg} invalid`;
  }

  return null;
};


// Returns null for no error or string for error message
export const validateMatrixModelsDataRoutes = ({ actions, matrixAxisDataRoutes, matrixModel }: {
  actions: LegacyAction[];
  matrixModel: LegacyMatrixModelResponse;
  matrixAxisDataRoutes: Record<string, LegacyMatrixAxisDataRoutes>;
}): string => {
  const misconfigured = { x: false, y: false };
  const missing = { x: false, y: false };

  ['x', 'y'].forEach(axis => {
    const matrixAxisDataRoute = matrixAxisDataRoutes[matrixModel.actionId]?.[axis];
    // This part of the function validates our code for setting data routes which
    // we technically would never encounter this error due to how `AxisHeaderDropdown` works.
    // This is a copy and refactor of `areMatrixModelDataRoutesValid` in Flow:
    if (matrixAxisDataRoutes[matrixModel.actionId] && matrixAxisDataRoute) {
      // get action to check if it is a service
      // if it is a service, ensure the attribute is set
      const sourceAction = actions.find(
        action => action.id === matrixAxisDataRoute.sourceId,
      );

      if (sourceAction.type === 'run' && !matrixAxisDataRoute.attribute.id) {
        misconfigured[axis] = true;
      }
    }

    // This part of the function validates user error for not linking an axis:
    if (!matrixAxisDataRoute) {
      const is1DMatrix = isMatrix1Dimensional({
        axisHeader: matrixModel.x_axis_header,
        axisHeaderDataType: matrixModel.x_axis_header_data_type,
        order: matrixModel.x_order,
      });

      if (axis === 'x' && !is1DMatrix) missing.x = true;
      if (axis === 'y') missing.y = true;
    }
  });

  if (misconfigured.x || misconfigured.y) {
    const axisErrorMsg = misconfigured.x && misconfigured.y
      ? 'X and Y axes are'
      : `${misconfigured.x ? 'X' : 'Y'} axis is`;

    return `Matrix ${matrixModel.name}: ${axisErrorMsg} misconfigured, please relink axis`;
  }

  if (missing.x || missing.y) {
    const axisErrorMsg = missing.x && missing.y
      ? 'X and Y axes are'
      : `${missing.x ? 'X' : 'Y'} axis is`;

    return `Matrix ${matrixModel.name}: ${axisErrorMsg} missing, please link action`;
  }

  return null;
};

export const validateLegacyMatrixModels = ({ actions, matrixModels, matrixAxisDataRoutes }: {
  actions: LegacyAction[];
  matrixModels: LegacyMatrixModelResponse[];
  matrixAxisDataRoutes: Record<string, LegacyMatrixAxisDataRoutes>;
}): string[] => {
  const errors = [];
  matrixModels.forEach(matrixModel => {
    const axisError = validateMatrixModelsAxis(matrixModel);
    const dataRouteError = validateMatrixModelsDataRoutes({
      actions, matrixModel, matrixAxisDataRoutes,
    });
    if (axisError) errors.push(axisError);
    if (dataRouteError) errors.push(dataRouteError);
  });

  return errors;
};
